import React, { useEffect, useState, useRef } from 'react';
import { db, storage } from '../../pages/app/protected/firebase';
import { collection, getDocs, addDoc, query, where, doc, getDoc, onSnapshot, setDoc, getDoc as fetchDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const AddProduct = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const { companyId } = useAuth();
  const fileInputRef = useRef(null);
  const barcodeInputRef = useRef(null);
  const scannerRef = useRef(null);
  const readerRef = useRef(null);

  const [productType, setProductType] = useState('');
  const [hasVariants, setHasVariants] = useState(false);
  const [trackInventory] = useState(true);
  const [showNewProductType, setShowNewProductType] = useState(false);
  const [newProductType, setNewProductType] = useState('');
  const [productTypes, setProductTypes] = useState([]);
  const [isAddingType, setIsAddingType] = useState(false);

  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [price, setPrice] = useState('');
  const [purchase, setPurchase] = useState('');
  const [brand, setBrand] = useState('');
  const [qty, setQty] = useState(0);
  const [productImages, setProductImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [barcode, setBarcode] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [onlineOrdering, setOnlineOrdering] = useState(true); // Default to true
  const [optionGroups, setOptionGroups] = useState([]);
  const [variants, setVariants] = useState([]);
  const [isGeneratingBarcode, setIsGeneratingBarcode] = useState(false);
  const [variantGenerating, setVariantGenerating] = useState(null);

  // 🔹 Fetch product types (live)
  useEffect(() => {
    if (!companyId) return;
    const typesRef = collection(db, 'companies', companyId, 'companySettings', 'inventory', 'productTypes');
    const unsub = onSnapshot(typesRef, (snap) => {
      const types = snap.docs.map((doc) => doc.data().name);
      setProductTypes(types);
    });
    return () => unsub();
  }, [companyId]);

  // 🔹 Check if SKU exists
  const checkSKUExists = async (sku) => {
    if (!companyId) return false;
    const q = query(collection(db, 'companies', companyId, 'inventory', 'data', 'products'), where('sku', '==', sku));
    const snap = await getDocs(q);
    return !snap.empty;
  };

  const checkBarcodeUnique = async (barcodeValue, currentProductId) => {
    if (!companyId || !barcodeValue) return true;
    const ref = doc(db, 'companies', companyId, 'barcodes', barcodeValue);
    const snap = await fetchDoc(ref);
    if (!snap.exists()) return true;
    const data = snap.data();
    if (data.productId && data.productId !== currentProductId) return false;
    return true;
  };

  const computeEan13 = (digits) => {
    const payload = digits.padStart(12, '0').slice(-12);
    const sum = payload.split('').reduce((acc, num, idx) => {
      const n = Number(num);
      return acc + (idx % 2 === 0 ? n : n * 3);
    }, 0);
    const checksum = (10 - (sum % 10)) % 10;
    return `${payload}${checksum}`;
  };

  const generateCandidateBarcode = () => {
    const seed = `${Date.now()}${Math.floor(Math.random() * 1_000_000)
      .toString()
      .padStart(6, '0')}`;
    return computeEan13(seed);
  };

  const isBarcodeTakenLocally = (candidate, skipVariantIndex = null) => {
    if (!candidate) return false;
    if (barcode === candidate) return true;
    return variants.some((v, idx) => idx !== skipVariantIndex && v.barcode === candidate);
  };

  const generateUniqueBarcode = async (skipVariantIndex = null) => {
    for (let attempt = 0; attempt < 6; attempt++) {
      const candidate = generateCandidateBarcode();
      if (isBarcodeTakenLocally(candidate, skipVariantIndex)) continue;
      const ok = await checkBarcodeUnique(candidate, null);
      if (ok) return candidate;
    }
    throw new Error('Unable to generate a unique barcode right now. Please try again.');
  };

  // 🔹 Handle file input
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setProductImages((prev) => [...prev, ...files]);
    setImagePreviews((prev) => [...prev, ...files.map((file) => URL.createObjectURL(file))]);
  };

  const handleRemoveImage = (idx) => {
    setProductImages((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const toAllCaps = (val = '') => val.toUpperCase();

  const handleImageClick = () => fileInputRef.current.click();

  const uploadImages = async () => {
    if (!productImages.length || !companyId) return [];
    const uploads = productImages.map(async (file) => {
      const imageRef = ref(storage, `companies/${companyId}/uploads/${uuidv4()}`);
      await uploadBytes(imageRef, file);
      return await getDownloadURL(imageRef);
    });
    return Promise.all(uploads);
  };

  const addOptionGroup = () => {
    setOptionGroups(prev => [...prev, { id: uuidv4(), name: '', values: [{ label: '', price: 0 }] }]);
  };

  const updateOptionGroup = (groupId, updater) => {
    setOptionGroups(prev => prev.map(g => g.id === groupId ? updater(g) : g));
  };

  const removeOptionGroup = (groupId) => {
    setOptionGroups(prev => prev.filter(g => g.id !== groupId));
    setVariants([]);
  };

  const generateVariants = () => {
    const validGroups = optionGroups
      .filter(g => g.name.trim() && g.values.some(v => (v.label ?? v).toString().trim()))
      .map(g => ({
        ...g,
        values: g.values
          .map(v => ({
            label: (v.label ?? v).toString().trim(),
            price: Number(v.price || 0),
          }))
          .filter(v => v.label),
      }));
    if (!validGroups.length) {
      setVariants([]);
      return;
    }

    const combos = validGroups.reduce((acc, group) => {
      const next = [];
      group.values.forEach(val => {
        if (!acc.length) {
          next.push([{ group: group.name.trim(), value: val.label, price: val.price }]);
        } else {
          acc.forEach(c => next.push([...c, { group: group.name.trim(), value: val.label, price: val.price }]));
        }
      });
      return next;
    }, []);

    setVariants(combos.map(combo => {
      const priceDelta = combo.reduce((sum, item) => sum + Number(item.price || 0), 0);
      return {
        id: uuidv4(),
        options: combo.map(({ group, value }) => ({ group, value })),
        priceDelta,
        price: Number(price) + priceDelta,
        qty: 0,
        barcode: '',
        sku: '',
      };
    }));
  };
  

  // 🔹 Handle barcode manually (hardware scanner)
  const handleBarcodeChange = (e) => setBarcode(e.target.value);

  const handleGenerateBarcode = async () => {
    setError('');
    setIsGeneratingBarcode(true);
    try {
      const candidate = await generateUniqueBarcode(null);
      setBarcode(candidate);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to generate barcode.');
    } finally {
      setIsGeneratingBarcode(false);
    }
  };

  // 🔹 Camera-based barcode scanning
  useEffect(() => {
    if (!showScanner) return;

    const html5QrCode = new Html5Qrcode("reader");
    scannerRef.current = html5QrCode;

    const onScanSuccess = (decodedText) => {
      if (decodedText) {
        setBarcode(decodedText);
        setShowScanner(false);
        barcodeInputRef.current?.focus(); // Focus the barcode input
      }
    };

    html5QrCode
      .start(
        { facingMode: "environment" },
        {
          fps: 8,
          qrbox: { width: 350, height: 250 },
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.QR_CODE,
          ],
        },
        onScanSuccess,
        (errorMsg) => {
          // Ignore "no barcode detected" logs — they’re normal
        }
      )
      .catch((err) => console.error("Camera start failed:", err));

    return () => {
      html5QrCode
        .stop()
        .then(() => html5QrCode.clear())
        .catch(() => {}); // ignore stop errors
    };
  }, [showScanner]);
  

// Add this function with your other handler functions
const handleAddNewProductType = async () => {
  if (!newProductType.trim() || !companyId) return;
  
  setIsAddingType(true);
  try {
    // Check if type already exists
    if (productTypes.includes(newProductType)) {
      alert('This product type already exists');
      return;
    }

    const companyRef = doc(db, 'companies', companyId);
    const companySnap = await getDoc(companyRef);
    const plan = companySnap.data()?.subscription?.plan || 'Free Trial';
    const quota = quotas[plan] || quotas['Free'];

    // Check quota
    const typesRef = collection(db, 'companies', companyId, 'companySettings', 'inventory', 'productTypes');
    const typesSnap = await getDocs(typesRef);
    
    if (quota.productType !== 'ꝏ' && typesSnap.size >= quota.productType) {
      alert('Product type limit reached for your plan');
      return;
    }

    // Add new product type
    await addDoc(typesRef, { name: newProductType });
    
    // Update local state
    setProductTypes(prev => [...prev, newProductType]);
    setProductType(newProductType);
    setNewProductType('');
    setShowNewProductType(false);
  } catch (error) {
    console.error('Error adding product type:', error);
    alert('Failed to add product type. Please try again.');
  } finally {
    setIsAddingType(false);
  }
};

  // 🔹 Submit product
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    setLoading(true);
    setError('');

    try {
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      const plan = companySnap.data()?.subscription?.plan || 'Free Trial';
      const quota = quotas[plan] || quotas['Free'];

      const productSnap = await getDocs(collection(db, 'companies', companyId, 'inventory', 'data', 'products'));
      if (quota.product !== 'ꝏ' && productSnap.size >= quota.product) {
        setError('Product limit reached for your plan.');
        setLoading(false);
        return;
      }

      const exists = await checkSKUExists(sku);
      if (exists) {
        setError('SKU already exists.');
        setLoading(false);
        return;
      }

      // Validate barcodes uniqueness
      if (barcode) {
        const ok = await checkBarcodeUnique(barcode, null);
        if (!ok) {
          setError('Barcode already used by another product/variant.');
          setLoading(false);
          return;
        }
      }
      for (const v of variants) {
        if (v.barcode) {
          const ok = await checkBarcodeUnique(v.barcode, null);
          if (!ok) {
            setError(`Barcode ${v.barcode} already used.`);
            setLoading(false);
            return;
          }
        }
      }

      const imageUrls = await uploadImages();
      const imageUrl = imageUrls[0] || null;

      const totalQty = hasVariants && variants.length ? variants.reduce((sum, v) => sum + Number(v.qty || 0), 0) : parseInt(qty);
      const product = {
        sku,
        barcode: barcode || null,
        name,
        desc,
        price: parseFloat(price),
        brand,
        qty: totalQty,
        purchase: parseFloat(purchase),
        type: productType,
        hasVariants,
        trackInventory: true,
        baseSku: sku,
        onlineOrdering: onlineOrdering,
        image: imageUrl,
        images: imageUrls,
        createdAt: new Date().toISOString(),
        options: hasVariants
          ? optionGroups
              .filter(g => g.name.trim())
              .map(g => ({
                name: g.name.trim(),
                values: g.values
                  .map(v => ({
                    label: (v.label ?? v).toString().trim(),
                    price: Number(v.price || 0),
                  }))
                  .filter(v => v.label),
              }))
          : [],
        variants: hasVariants
          ? variants.map(v => {
              const delta = v.options.reduce((sum, opt) => {
                const group = optionGroups.find(g => g.name.trim() === opt.group);
                const matchVal = group?.values?.find(
                  val => (val.label ?? val).toString().trim() === opt.value
                );
                const valPrice = Number(matchVal?.price || 0);
                return sum + valPrice;
              }, 0);
              const safeDelta = Number.isFinite(delta) ? delta : 0;
              return {
                id: v.id,
                options: v.options,
                priceDelta: safeDelta,
                price: Number(price) + safeDelta,
                qty: Number(v.qty || 0),
                barcode: v.barcode || '',
                sku: v.sku || '',
              };
            })
          : [],
      };

      const productRef = await addDoc(collection(db, 'companies', companyId, 'inventory', 'data', 'products'), product);

      // Index barcodes
      if (product.barcode) {
        await setDoc(doc(db, 'companies', companyId, 'barcodes', product.barcode), {
          productId: productRef.id,
          type: 'product',
          companyId,
        });
      }
      if (hasVariants) {
        for (const v of product.variants) {
          if (v.barcode) {
            await setDoc(doc(db, 'companies', companyId, 'barcodes', v.barcode), {
              productId: productRef.id,
              variantId: v.id,
              type: 'variant',
              companyId,
            });
          }
        }
      }
      onClose();
    } catch (err) {
      console.error('Failed to save product:', err);
      setError('Failed to save product.');
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Focus barcode input (for hardware scanner)
  useEffect(() => {
    if (isOpen && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-[#161616] rounded-2xl max-w-3xl w-full text-white relative max-h-[90vh] flex flex-col">
        <button onClick={onClose} className="absolute top-4 right-4 text-white text-4xl">&times;</button>

        <div className="p-6 pt-10 space-y-4 overflow-y-auto border border-gray-700 rounded bg-gray-800">
          <h2 className="text-2xl font-bold">Add Product</h2>

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="border border-gray-700 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Basic Details</h3>
              {/* SKU */}
              <div>
                <label className="text-sm text-gray-400">SKU <span className="text-red-400">*</span></label>
              <input
                className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                required
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="SKU"
              />
            </div>

            {/* Barcode */}
            {!hasVariants && (
              <div>
                <label className="text-sm text-gray-400">Barcode</label>
                <div className="flex gap-2">
                  <input
                    ref={barcodeInputRef}
                    className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                    value={barcode}
                    onChange={handleBarcodeChange}
                    placeholder="Scan or enter barcode"
                  />
                  <button
                    type="button"
                    onClick={handleGenerateBarcode}
                    disabled={isGeneratingBarcode}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 rounded disabled:opacity-50"
                  >
                    {isGeneratingBarcode ? '...' : 'Auto'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowScanner(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 rounded"
                  >
                    Use Camera
                  </button>
                </div>
              </div>
            )}

            {/* Camera Modal */}
            {showScanner && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-80 z-50">
                <div className="bg-white rounded-lg p-4 w-11/12 max-w-md">
                  <h3 className="text-lg font-bold text-center text-gray-800 mb-3">Scan Barcode</h3>
                  <div id="reader" ref={readerRef} className="w-full rounded overflow-hidden" style={{ height: '300px' }} />
                  <button
                    type="button"
                    onClick={() => setShowScanner(false)}
                    className="mt-4 w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded"
                  >
                    Close Scanner
                  </button>
                </div>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="text-sm text-gray-400">Product Name *</label>
              <input
                className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Product Name"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-gray-400">Description</label>
              <textarea
                className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                placeholder="(optional)"
              />
            </div>

            </div>

            <div className="border border-gray-700 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Pricing</h3>
              {/* Price */}
              <div>
                <label className="text-sm text-gray-400">Selling Price (RM) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                  placeholder="RM"
                />
              </div>

              {/* Purchase */}
              <div>
                <label className="text-sm text-gray-400">Purchase Price (RM) *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={purchase}
                  onChange={(e) => setPurchase(e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                  placeholder="RM"
                />
              </div>
            </div>

            <div className="border border-gray-700 rounded-lg p-4 space-y-3">
              <h3 className="text-lg font-semibold">Inventory</h3>
              {/* Brand */}
              <div>
                <label className="text-sm text-gray-400">Brand</label>
                <input
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
                  placeholder="Brand (optional)"
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="text-sm text-gray-400">Quantity *</label>
                <input
                  type="number"
                  required
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className={`w-full p-2 rounded border-2 border-gray-400 ${
                    hasVariants ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-gray-800 text-white'
                  }`}
                  placeholder="Quantity"
                  disabled={hasVariants}
                />
                <div className="flex items-center gap-2 mt-2">
                  <div
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition ${hasVariants ? 'bg-purple-600' : 'bg-gray-600'}`}
                    onClick={() => {
                      setHasVariants((prev) => {
                        const next = !prev;
                        if (next) {
                          setQty(0);
                        }
                        return next;
                      });
                    }}
                  >
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${hasVariants ? 'translate-x-6' : ''}`} />
                  </div>
                  <span className="text-sm text-gray-300">Has options / variants</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-12 h-6 flex items-center rounded-full p-1 bg-purple-600">
                    <div className="bg-white w-4 h-4 rounded-full shadow-md transform duration-300 translate-x-6" />
                  </div>
                  <span className="text-sm text-gray-300">Track inventory (always on)</span>
                </div>
                {hasVariants && <p className="text-xs text-gray-400">Quantity can't be changed here. Update it in variants.</p>}
              </div>
            </div>

            {/* Options and Variants */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Options & Variants</h3>
                <button
                  type="button"
                  onClick={addOptionGroup}
                  disabled={!hasVariants}
                  className={`px-3 py-1 rounded text-white text-sm ${hasVariants ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 cursor-not-allowed'}`}
                  title={!hasVariants ? 'Enable "Has options / variants" first' : 'Add option group'}
                >
                  Add Option Group
                </button>
              </div>

              {!hasVariants && (
                <p className="text-sm text-gray-400">
                  Toggle on "Has options / variants" in Inventory to configure option groups and variants.
                </p>
              )}

              {hasVariants && (
                <>
                  {optionGroups.map((group) => (
                    <div key={group.id} className="bg-[#1f1f1f] p-3 rounded-lg space-y-2">
                      <div className="flex justify-between items-center gap-2">
                      <input
                        className="bg-[#111] rounded px-3 py-2 flex-1"
                        placeholder="Option name (e.g., Size)"
                        value={group.name}
                          onChange={(e) =>
                            updateOptionGroup(group.id, (g) => ({
                              ...g,
                              name: toAllCaps(e.target.value),
                            }))
                          }
                        />
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-600 text-sm"
                          onClick={() => removeOptionGroup(group.id)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="space-y-2">
                        {group.values.map((val, idx) => (
                          <div key={`${group.id}-${idx}`} className="flex items-center gap-2">
                            <input
                              className="bg-[#111] rounded px-3 py-2 flex-1"
                              placeholder="Value (e.g., XL)"
                              value={val.label ?? val}
                              onChange={(e) =>
                                updateOptionGroup(group.id, (g) => {
                                  const newVals = [...g.values];
                                  newVals[idx] = {
                                    label: toAllCaps(e.target.value),
                                    price: Number(newVals[idx]?.price || 0),
                                  };
                                  return { ...g, values: newVals };
                                })
                              }
                            />
                            <div className="flex flex-col">
                              <label className="text-[11px] text-gray-400 px-1">Δ Price</label>
                              <input
                                type="number"
                                step="0.01"
                                className="bg-[#111] rounded px-2 py-2 w-28"
                                placeholder="Δ Price"
                                value={val.price || 0}
                                onChange={(e) =>
                                  updateOptionGroup(group.id, (g) => {
                                    const newVals = [...g.values];
                                    newVals[idx] = {
                                      label: toAllCaps(newVals[idx]?.label || val.label || val),
                                      price: Number(e.target.value || 0),
                                    };
                                    return { ...g, values: newVals };
                                  })
                                }
                              />
                            </div>
                            <button
                              type="button"
                              className="text-red-400 hover:text-red-600 text-sm"
                              onClick={() =>
                                updateOptionGroup(group.id, (g) => ({
                                  ...g,
                                  values: g.values.filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="text-sm text-blue-400 hover:text-blue-600"
                          onClick={() =>
                            updateOptionGroup(group.id, (g) => ({
                              ...g,
                              values: [...g.values, { label: '', price: 0 }],
                            }))
                          }
                        >
                          + Add value
                        </button>
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={generateVariants}
                      disabled={!optionGroups.length}
                      className={`px-3 py-2 rounded text-white text-sm ${optionGroups.length ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 cursor-not-allowed'}`}
                    >
                      Regenerate Variants
                    </button>
                    {!optionGroups.length && (
                      <span className="text-xs text-gray-400">Add at least one option group to generate variants.</span>
                    )}
                  </div>

                  {hasVariants && variants.length === 0 && (
                    <div className="text-xs text-yellow-400 bg-yellow-900/30 border border-yellow-800/50 rounded p-2">
                      No variants generated yet. Add option values then click "Generate Variants" so stock and pricing use the variants.
                    </div>
                  )}

                  {variants.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm">Variants</h4>
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                  {variants.map((variant, idx) => (
                    <div key={variant.id} className="bg-[#1f1f1f] rounded p-3 space-y-2">
                      <div className="text-xs text-gray-300">
                        {variant.options.map((o) => `${o.group}: ${o.value}`).join(', ')}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <label className="text-xs text-gray-300 flex flex-col gap-1">
                          <span>Qty</span>
                          <input
                            type="number"
                            min="0"
                            className="bg-[#111] rounded px-3 py-2"
                            value={variant.qty}
                            onChange={(e) =>
                              setVariants((prev) =>
                                prev.map((v, i) =>
                                  i === idx ? { ...v, qty: e.target.value } : v
                                )
                              )
                            }
                          />
                        </label>
                        <label className="text-xs text-gray-300 flex flex-col gap-1">
                          <span>Barcode</span>
                          <input
                            type="text"
                            className="bg-[#111] rounded px-3 py-2"
                            value={variant.barcode || ''}
                            onChange={(e) =>
                              setVariants((prev) =>
                                prev.map((v, i) =>
                                  i === idx ? { ...v, barcode: e.target.value } : v
                                )
                              )
                            }
                            placeholder="Variant barcode"
                          />
                          <button
                            type="button"
                            className="mt-1 text-xs text-blue-400 hover:text-blue-200 text-left disabled:opacity-50"
                            disabled={variantGenerating === idx}
                            onClick={async () => {
                              setError('');
                              setVariantGenerating(idx);
                              try {
                                const candidate = await generateUniqueBarcode(idx);
                                setVariants((prev) =>
                                  prev.map((v, i) =>
                                    i === idx ? { ...v, barcode: candidate } : v
                                  )
                                );
                              } catch (err) {
                                console.error(err);
                                setError(err.message || 'Failed to generate variant barcode.');
                              } finally {
                                setVariantGenerating(null);
                              }
                            }}
                          >
                            {variantGenerating === idx ? 'Generating…' : 'Auto-generate'}
                          </button>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
                </>
              )}
            </div>

            {/* Product Type */}
            <div>
              <label className="block text-sm text-gray-400">Product Type *</label>
              <select
                required
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                className="w-full p-2 rounded bg-gray-800 text-white border-2 border-gray-400"
              >
                <option value="">Select a type</option>
                {productTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setShowNewProductType(!showNewProductType)}
                className="text-sm text-purple-400 mt-2"
              >
                + Add New Type
              </button>
              {showNewProductType && (
                <div className="mt-2 space-y-2">
                  <input
                    type="text"
                    value={newProductType}
                    onChange={(e) => setNewProductType(e.target.value)}
                    className="w-full p-2 rounded bg-gray-700 text-white"
                    placeholder="New Product Type"
                  />
                  <button
  type="button"
  onClick={handleAddNewProductType}
  disabled={!newProductType.trim() || isAddingType}
  className={`w-full py-2 rounded text-white flex items-center justify-center ${
    isAddingType ? 'bg-purple-400' : 'bg-purple-600 hover:bg-purple-700'
  }`}
>
  {isAddingType ? (
    <>
      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Adding...
    </>
  ) : (
    'Add Type'
  )}
</button>
                </div>
              )}
            </div>

            {/* Images */}
            <div>
              <label className="text-sm text-gray-400">Product Images</label>
              <div
                onClick={handleImageClick}
                className="border-dashed border-2 p-4 rounded text-center text-gray-400 cursor-pointer hover:border-purple-600"
              >
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  multiple
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {imagePreviews.length ? (
                  <div className="flex flex-wrap gap-3 justify-center">
                    {imagePreviews.map((src, idx) => (
                      <div key={src + idx} className="relative">
                        <img src={src} alt="Preview" className="h-24 w-24 object-contain rounded bg-gray-900 border border-gray-700" />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage(idx);
                          }}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-6 h-6 text-xs"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <p>Click to upload</p>
                    <p className="text-xs text-gray-500">PNG, JPG (max 2MB) • You can select multiple</p>
                  </>
                )}
              </div>
            </div>

            {/* Online Ordering Toggle */}
            <div className="flex items-center justify-between bg-gray-700 p-3 rounded">
              <div>
                <label className="text-sm font-medium">Available for Online Ordering</label>
                <p className="text-xs text-gray-400">Allow customers to order this product online</p>
              </div>
              <button
                type="button"
                onClick={() => setOnlineOrdering(!onlineOrdering)}
                className={`w-12 h-6 flex items-center rounded-full p-1 duration-300 ${onlineOrdering ? 'bg-purple-600' : 'bg-gray-600'}`}
              >
                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${onlineOrdering ? 'translate-x-6' : ''}`} />
              </button>
            </div>

            <button
              type="submit"
              className="w-full py-2 bg-purple-600 hover:bg-purple-700 rounded text-white"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Add Product'}
            </button>
            {error && <p className="text-red-500 mt-2">{error}</p>}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;
